<script setup lang="ts">
import { useRoute } from 'vue-router'
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'
import type { InvoiceFirstPage } from '@/types'
import { initYandexMetrika } from '@/helpers/yandexMetrika'

import UiHeader from '@/components/UiHeader/UiHeader.vue'
import UiProductCard from '@/components/UiProductCard/UiProductCard.vue'
import UiAntForm from '@/components/UiForm/UiAntForm.vue'
import UiFooter from '@/components/UiFooter/UiFooter.vue'
import UiHero from '@/components/UiHero/UiHero.vue'

// Первая страница счёта: данные клиента уже заполнены (например, из сделки amoCRM),
// клиент проверяет их, принимает оферту и переходит к выбору способа оплаты.
// Оплаченный или уже начатый счёт сразу открывает обычную страницу счёта.

const route = useRoute()
const unavailable = ref<boolean>(false)
const invoice = ref<InvoiceFirstPage | null>()
const pageLoader = ref(true)

async function getInvoice(uuid: string | string[]) {
  try {
    const { data } = await axios.get<InvoiceFirstPage>(`/i/${uuid}`)

    if (!data.awaiting_first_page) {
      window.location.href = data.link
      return
    }

    invoice.value = data

    if (data.yandex_metrika_id) {
      initYandexMetrika(data.yandex_metrika_id)
    }
  } catch (error) {
    unavailable.value = true
  }
}

const offerUrls = computed<string[]>(() => {
  const offers: string[] = []

  if (!invoice.value) {
    return offers
  }

  for (const item of invoice.value.items) {
    if (item.offer_url) {
      offers.push(item.offer_url)
    } else if (item.legal_entity.offer) {
      offers.push(item.legal_entity.offer)
    }
  }

  return [...new Set(offers)]
})

// ФИО в amoCRM — одна строка; раскладываем по полям формы, клиент поправит.
const initialValues = computed(() => {
  const contact = invoice.value?.contact
  if (!contact) return undefined

  const parts = (contact.name ?? '').trim().split(/\s+/).filter(Boolean)
  const [surname, name, patronymic] =
    parts.length >= 3 ? [parts[0], parts[1], parts.slice(2).join(' ')]
    : parts.length === 2 ? [parts[0], parts[1], '']
    : ['', parts[0] ?? '', '']

  return {
    surname,
    name,
    patronymic,
    phone: contact.phone ?? '',
    country_code: contact.country_code ?? 'RU',
    email: contact.email ?? ''
  }
})

onMounted(async () => {
  try {
    await getInvoice(route.params.uuid)
  } finally {
    pageLoader.value = false
  }
})
</script>

<template>
  <div v-if="pageLoader || invoice" class="payment-cabinet">
    <div>
      <ui-header
        :logo-src="invoice?.merchant.logo_url ?? undefined"
        :logo-loading="pageLoader"
      />

      <template v-if="invoice">
        <ui-product-card
          :items="invoice.items"
          :total-price="invoice.price"
          :currency="invoice.currency"
          :show-monthly-payment="invoice.show_monthly_payment"
          :monthly-payment="invoice.monthly_payment"
        />

        <ui-ant-form
          :form-options="null"
          :offer-urls="offerUrls"
          :show-accept-all="invoice.show_accept_all_terms"
          :submit-url="`/i/${invoice.id}`"
          :initial-values="initialValues"
        />

        <ui-footer />
      </template>
    </div>
  </div>

  <div v-if="unavailable" class="payment-cabinet">
    <div class="container">
      <ui-hero title="Страница оплаты недоступна" />
    </div>
  </div>
</template>
